"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatPriceCents } from "@tippo/logic";
import type { TPricingTier } from "@/lib/sanity/pricing";
import type { TBillingInterval } from "./BillingIntervalToggle";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { Check } from "@phosphor-icons/react";
import { animate, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

interface IPricingCardProps {
  tier: TPricingTier;
  interval: TBillingInterval;
  onSelect?: (slug: string, interval: TBillingInterval) => void;
  isLoading?: boolean;
  /** When true, primary CTA is shown; when false, card may be display-only (e.g. marketing). */
  showCta?: boolean;
  /** For checkout flow: pass restaurantId so CTA triggers checkout. */
  restaurantId?: string | null;
  /** Number of active subscriptions for this tier (only meaningful when tier has a subscriptionCap). */
  soldCount?: number | undefined;
  /** Override CTA label (e.g. "Upgrade", "Downgrade"). Falls back to tier.ctaText. */
  ctaLabel?: string;
  /** Render as the user's currently active plan: muted, disabled, "Current plan" label. */
  isCurrent?: boolean;
  className?: string;
}

/**
 * Single tier card: name, description, price (monthly or yearly), features, optional CTA.
 * Uses Sanity display amounts when present; otherwise shows a fallback.
 */
export function PricingCard({
  tier,
  interval,
  onSelect,
  isLoading = false,
  showCta = true,
  soldCount,
  ctaLabel,
  isCurrent = false,
  className,
}: IPricingCardProps): React.ReactElement {
  const prefersReducedMotion = useReducedMotion();
  const currency = tier.currency ?? "CAD";
  const isYearly = interval === "yearly";

  const cap = tier.subscriptionCap;
  const hasCap = cap != null && cap > 0 && soldCount != null;
  const isSoldOut = hasCap && soldCount >= cap;

  const presoldBase = 16;
  const threshold = 32;

  const claimedForDisplay = hasCap
    ? (() => {
        const trueClaimed = presoldBase + soldCount!;
        const easedClaimed = presoldBase + Math.floor(soldCount! / 2);

        const claimedUnclamped =
          soldCount! >= threshold ? trueClaimed : Math.min(easedClaimed, trueClaimed);

        return Math.min(claimedUnclamped, cap!);
      })()
    : null;

  const amountCents = isYearly ? tier.amountYearly : tier.amountMonthly;
  const hasPrice = amountCents != null && amountCents > 0;

  // Both intervals display an effective per-month price; the yearly note below
  // the price explains the annual billing.
  const priceLabel = hasPrice
    ? formatPriceCents(
        isYearly ? Math.round(amountCents / 12) : amountCents,
        { currency }
      )
    : null;

  const [animatedClaimed, setAnimatedClaimed] = useState(0);

  useEffect(() => {
    if (!hasCap || claimedForDisplay == null) return;

    if (prefersReducedMotion) {
      setAnimatedClaimed(claimedForDisplay);
      return;
    }

    const controls = animate(0, claimedForDisplay, {
      duration: 0.8,
      ease: "easeOut",
      onUpdate: (latest) => setAnimatedClaimed(Math.round(latest)),
    });

    return () => controls.stop();
  }, [claimedForDisplay, hasCap, prefersReducedMotion]);

  return (
    <motion.div
      {...(!prefersReducedMotion && { whileHover: { y: -6, scale: 1.01 } })}
      transition={{ duration: 0.2 }}
    >
    <Card
      className={cn(
        "flex h-full flex-col rounded-2xl shadow-sm transition-shadow duration-200 hover:shadow-lg",
        tier.isPopular && "border-primary/45 shadow-lg",
        className
      )}
    >
      <CardHeader>
        <div className="flex min-h-6 items-center justify-between gap-2.5">
          <CardTitle className="text-xs font-semibold uppercase leading-none tracking-[.08em] text-primary">
            {tier.name}
          </CardTitle>
          {tier.isPopular && (
            <span className="rounded-full bg-primary/10 px-[11px] py-[5px] text-[11.5px] font-semibold leading-none text-primary">
              Most popular
            </span>
          )}
        </div>
        {tier.description && (
          <CardDescription className="mt-2">{tier.description}</CardDescription>
        )}
        {priceLabel && (
          <div className="mt-4 flex items-baseline gap-1.5">
            <span className="text-4xl font-bold leading-none tracking-tight text-foreground tabular-nums">
              {priceLabel}
            </span>
            <span className="text-sm font-medium leading-none text-muted-foreground">
              /month
            </span>
          </div>
        )}
        {priceLabel && isYearly && (
          <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-primary/90">
            billed annually — 16% off
          </p>
        )}
        {!hasPrice && (
          <p className="mt-4 text-sm text-muted-foreground">
            Contact us for pricing
          </p>
        )}
      </CardHeader>
      <CardContent className="flex-1">
        {tier.features && tier.features.length > 0 && (
          <ul className="space-y-2">
            {tier.features.map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-muted-foreground"
              >
                <Check
                  className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                  aria-hidden
                />
                <span>{f.text}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {hasCap && (
        <div className="px-6 pb-4">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
            <span>
              <span className="font-semibold text-foreground">
                {animatedClaimed}
              </span>{" "}
              / {cap} spots claimed
            </span>
            {isSoldOut && (
              <span className="font-medium text-destructive">Sold out</span>
            )}
          </div>
          <Progress value={(animatedClaimed / cap) * 100} className="h-2" />
        </div>
      )}
      {showCta && (
        <CardFooter>
          <Button
            className="w-full"
            variant={isCurrent ? "outline" : tier.isPopular ? "default" : "outline"}
            onClick={() => !isCurrent && onSelect?.(tier.slug, interval)}
            disabled={isLoading || isSoldOut || isCurrent || !onSelect}
          >
            {isCurrent
              ? "Current plan"
              : isSoldOut
                ? "Sold out"
                : (ctaLabel ?? tier.ctaText)}
          </Button>
        </CardFooter>
      )}
    </Card>
    </motion.div>
  );
}
